import axios, { Axios, AxiosRequestHeaders } from 'axios';
import { AppRouter } from '@infuse/ui/router';

const { VITE_APP_CONTEXT_PATH = null } = (import.meta as unknown as { env: Record<string, string> }).env;
const { VITE_IAM_CONTEXT_PATH = null } = (import.meta as unknown as { env: Record<string, string> }).env;
const { VITE_BROKER_CONTEXT_PATH = null } = (import.meta as unknown as { env: Record<string, string> }).env;

export type ApiClientTokenFactory = () => Promise<string | null>;

let currentTokenFactory: ApiClientTokenFactory = async () => null;

export const setTokenFactory = (tokenFactory: ApiClientTokenFactory) => {
  currentTokenFactory = tokenFactory;
};

export const baseUrlPrefix = (contextPath: string | null = null) => {
  // This fixes deployments for which we package the ui within the container, like the broker.
  // If the container is running with a different root context, the HTML base-tag in the index.html
  // is set when the container is started based on the env parameter SERVER_SERVLET_CONTEXT_PATH
  // See index.html: <base id="custom_base" href="">
  const customBaseUrl: string | undefined = (document.getElementById('custom_base') as HTMLAnchorElement | null)?.href;

  // The '<base id="custom_base" href="">' has priority
  if (customBaseUrl) {
    return customBaseUrl.replace(/\/$/, '') + (contextPath ? contextPath : '');
  }

  // Then the context path environment variable, with final fallback to empty string
  return contextPath ?? '';
};

export const createClient = (baseUrl: string, headers?: Record<string, string>): Axios => {
  const client = axios.create({
    baseURL: baseUrlPrefix(VITE_APP_CONTEXT_PATH) + baseUrl,
    headers
  });

  client.interceptors.request.use(async (request) => {
    const token = await currentTokenFactory();

    if (token) {
      // TODO proper fallback as this is just ignoring the typing.
      request.headers ||= {} as AxiosRequestHeaders;
      request.headers.Authorization = token;
    }

    return request;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const isBasicAuth = (await currentTokenFactory())?.startsWith('Basic '); // instanceof BasicAuthAuthenticator creates a circular dependency
      if (!isBasicAuth) {
        return Promise.reject(error); // we're not using basic auth, so no special handling
      }

      // if we get a 401 response, redirect to not-allowed page
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        await AppRouter.push({ name: 'not-allowed' });
      }

      return Promise.reject(error); // reject to allow local handling
    }
  );

  return client;
};

export const createUnauthenticatedClient = (baseUrl: string): Axios => {
  return axios.create({
    baseURL: baseUrlPrefix(VITE_APP_CONTEXT_PATH) + baseUrl
  });
};

export const createIamClient = (baseUrl: string): Axios => {
  const client = axios.create({
    baseURL: baseUrlPrefix(VITE_IAM_CONTEXT_PATH || VITE_APP_CONTEXT_PATH) + baseUrl
  });

  client.interceptors.request.use(async (request) => {
    const token = await currentTokenFactory();

    if (token) {
      // TODO proper fallback as this is just ignoring the typing.
      request.headers ||= {} as AxiosRequestHeaders;
      request.headers.Authorization = token;
    }

    return request;
  });

  return client;
};
